import React, {useState, useEffect} from 'react';
import {View, Text, TouchableOpacity, useWindowDimensions, Keyboard, Alert, Platform} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import ModernButton from '~components/ModernButton';
import ModernCard from '~components/ModernCard/ModernCard';
import {FadeIn} from '~components/MicroInteractions';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, borderRadius, typography, shadows} from '~constants/design';
import {SCREENS} from '~constants';
import {NavigationProps} from '~types/navigation/navigation.type';
import LinearGradient from 'react-native-linear-gradient';
import {PyxiLabel} from '~assets/icons';
import {OtpInput} from 'react-native-otp-entry';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {useVerifyOtp} from '~hooks/auth/useVerifyOtp';
import {useUserStore} from '~providers/userStore/zustand';
import {BASE_API_URL} from '@env';
import {useGetUserById} from '~hooks/user/useGetUserById';
import {jwtDecode} from 'jwt-decode';
import {logEvent, setUserId} from '~Utils/firebaseAnalytics';
import {ANALYTICS_EVENTS, ANALYTICS_PARAMS} from '~constants';

const OtpVerification: React.FC = () => {
  const {colors} = useTheme();
  const {t} = useTranslation();
  const {height} = useWindowDimensions();
  const navigation = useNavigation<NavigationProps>();
  const route = useRoute();
  const {email, password} = (route.params || {}) as {email: string; password: string};

  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const {mutateAsync: verifyOtp, isLoading: verifyLoading} = useVerifyOtp();
  const {setAccessToken, setUser} = useUserStore();

  // Simple resend timer logic
  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleVerify = async () => {
    Keyboard.dismiss();
    setIsLoading(true);
    try {
      const result = await verifyOtp({email, password, otp});
      if (result && result.access_token) {
        const token = result.access_token;
        // Decode JWT and get user id
        const jwt = token.replace(/^PYXI_/, '');
        console.log(jwt, 'jwt');

        const jwtPayload = jwtDecode(jwt);
        console.log(jwtPayload, 'jwt');
        const userId = jwtPayload.sub;

        logEvent(ANALYTICS_EVENTS.LOGIN_SUCCEEDED, {
          [ANALYTICS_PARAMS.AUTH_METHOD]: 'otp',
          [ANALYTICS_PARAMS.CUSTOMER_ID]: userId,
        });
        setUserId(userId);

        // Fetch full user from API
        console.log(`${BASE_API_URL || ''}users/${userId}`, 'url');
        console.log(token.startsWith('Bearer') ? token : `Bearer ${token}`, 'token');

        const userResponse = await fetch(`${BASE_API_URL}users/${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        });
        if (!userResponse.ok) {
          throw new Error('Failed to fetch user profile');
        }
        const fullUser = await userResponse.json();
        setAccessToken(token);
        setUser(fullUser);
      }
      setIsLoading(false);
      Alert.alert('Success', 'OTP Verified!');
    } catch (error: any) {
      setIsLoading(false);
      logEvent(ANALYTICS_EVENTS.LOGIN_FAILED, {
        [ANALYTICS_PARAMS.AUTH_METHOD]: 'otp',
        [ANALYTICS_PARAMS.ERROR_CODE]: error.message || 'otp_verification_failed',
      });
      Alert.alert('Verification Failed', error.message || 'Please try again.');
    }
  };

  const handleResend = () => {
    setResendTimer(30); // 30 seconds cooldown
    Alert.alert('OTP Sent', 'A new OTP has been sent to your email.');
  };

  const styles = {
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    keyboardContainer: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    scrollContainer: {
      flexGrow: 1,
      minHeight: height,
      paddingBottom: spacing.xl,
    },
    heroSection: {
      height: height * 0.28,
      position: 'relative' as const,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      overflow: 'hidden' as const,
    },
    heroGradient: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    heroContent: {
      flex: 1,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      zIndex: 2,
      paddingHorizontal: spacing.lg,
    },
    logoContainer: {
      alignItems: 'center' as const,
      marginBottom: spacing.md,
      transform: [{scale: 1.1}],
    },
    formSection: {
      flex: 1,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.xxxxxxl,
    },
    formCard: {
      marginBottom: spacing.lg,
    },
    formTitle: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      textAlign: 'center' as const,
      marginBottom: spacing.sm,
      letterSpacing: -0.3,
    },
    formSubtitle: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.textSecondary,
      textAlign: 'center' as const,
      marginBottom: spacing.lg,
      opacity: 0.85,
      letterSpacing: 0.1,
      lineHeight: typography.fontSize.base * 1.4,
    },
    resendContainer: {
      flexDirection: 'row' as const,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      marginTop: spacing.md,
      marginBottom: spacing.lg,
    },
    resendText: {
      color: colors.textSecondary,
      fontSize: typography.fontSize.sm,
    },
    resendButton: {
      marginLeft: spacing.xs,
    },
    resendButtonText: {
      color: resendTimer > 0 ? colors.textSecondary : colors.primary,
      fontWeight: typography.fontWeight.semibold,
      fontSize: typography.fontSize.sm,
      opacity: resendTimer > 0 ? 0.5 : 1,
    },
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <KeyboardAwareScrollView
        style={styles.keyboardContainer}
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        enableOnAndroid={true}
        enableAutomaticScroll={true}
        keyboardOpeningTime={0}
        extraScrollHeight={Platform.OS === 'ios' ? 20 : 40}
        extraHeight={Platform.OS === 'ios' ? 20 : 40}>
        {/* Hero Section with logo and gradient */}
        <View style={styles.heroSection}>
          <LinearGradient
            colors={[colors.primary, colors.primaryLight, colors.warning, colors.warningLight]}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
            style={styles.heroGradient}
          />
          <View style={styles.heroContent}>
            <View style={styles.logoContainer}>
              <PyxiLabel />
            </View>
          </View>
        </View>
        <View style={styles.formSection}>
          <FadeIn delay={50} duration={300}>
            <ModernCard variant="default" padding="xl" style={{...styles.formCard, ...shadows.none}}>
              <Text style={styles.formTitle}>Verify your account</Text>
              <Text style={styles.formSubtitle}>Enter the 6-digit code sent to your email</Text>
              <OtpInput
                numberOfDigits={6}
                focusColor={colors.primary}
                onTextChange={setOtp}
                autoFocus={true}
                blurOnFilled={true}
                type="numeric"
                theme={{
                  containerStyle: {marginBottom: spacing.md, width: 'auto', alignSelf: 'center'},
                  pinCodeContainerStyle: {
                    borderRadius: borderRadius.md,
                    borderWidth: 1,
                    borderColor: colors.border,
                    backgroundColor: colors.surface,
                    width: 44,
                    height: 54,
                    marginHorizontal: spacing.xs,
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                  pinCodeTextStyle: {
                    color: colors.textPrimary,
                    fontSize: typography.fontSize.xl,
                    fontWeight: typography.fontWeight.semibold,
                    textAlign: 'center',
                  },
                  focusedPinCodeContainerStyle: {
                    borderColor: colors.primary,
                  },
                  filledPinCodeContainerStyle: {
                    borderColor: colors.primary,
                    backgroundColor: colors.primary + '10',
                  },
                  placeholderTextStyle: {
                    color: colors.textSecondary,
                    opacity: 0.5,
                  },
                }}
              />
              <View style={styles.resendContainer}>
                <Text style={styles.resendText}>Didn't receive the code?</Text>
                <TouchableOpacity style={styles.resendButton} onPress={handleResend} disabled={resendTimer > 0}>
                  <Text style={styles.resendButtonText}>
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                  </Text>
                </TouchableOpacity>
              </View>
              <ModernButton
                onPress={handleVerify}
                title="Verify"
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading || verifyLoading}
                hapticFeedback
                disabled={otp.length !== 6}
              />
            </ModernCard>
          </FadeIn>
        </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  );
};

export default OtpVerification;
